// AssetDetailDrawer.jsx — panel detail asset SDP (dibuka saat row Assets diklik).
// Data awal: kolom snapshot DB + raw_json + spec workstation (sudah di-join di /api/sdp/assets).
// Data live: GET /api/sdp/assets/:id/live → { asset, related_requests, errors }.
import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { authGetJson, dv, S, statusColor, Field, Attachments, DrawerShell } from "./sdpShared";

const nz = (v) => (v == null || v === "" || v === "null" ? "" : v);
const money = (v) => {
  const n = Number(v && v.value != null ? v.value : v);
  return n ? n.toLocaleString() : "";
};
const gb = (v) => (nz(v) ? `${v} GB` : "");

function Card({ title, children }) {
  // buang Field yang value-nya kosong, supaya kartu tanpa isi tidak tampil
  const kids = React.Children.toArray(children).filter((c) => c && !(c.props && "value" in c.props &&
    (c.props.value == null || c.props.value === "" || c.props.value === "null")));
  if (!kids.length) return null;
  return (
    <div style={S.card}>
      <div style={S.h}>{title}</div>
      <div style={S.grid}>{kids}</div>
    </div>
  );
}

export default function AssetDetailDrawer({ row, onClose, onOpenTicket }) {
  const snap = (() => { try { return JSON.parse(row.raw_json || "{}"); } catch (e) { return {}; } })();
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = async () => {
    setLoading(true); setLoadErr("");
    try { setLive(await authGetJson(`/api/sdp/assets/${encodeURIComponent(row.id)}/live`)); }
    catch (e) { setLoadErr(e.message); }
    setLoading(false);
  };
  useEffect(() => { setLive(null); load(); /* eslint-disable-next-line */ }, [row.id]);

  const a = (live && live.asset) || snap || {};
  const errs = (live && live.errors) || {};
  const pick = (col, obj) => nz(row[col]) || dv(obj);
  const state = pick("state", a.state);
  const user = a.user || {};
  const adapters = Array.isArray(a.network_adapters) ? a.network_adapters : [];
  const related = (live && live.related_requests) || [];
  const lastAudit = a.last_audit || a.last_asset_audit || {};

  return (
    <DrawerShell
      label={`Asset ${row.name}`}
      loading={loading}
      onReload={load}
      onClose={onClose}
      title={row.name || a.name || "(no name)"}
      top={<>
        <span style={{ fontWeight: 700, color: "rgb(var(--brand-700))" }}>{pick("product_type", a.product_type) || "Asset"}</span>
        {state && <span style={{ ...S.badge, ...statusColor(state) }}>{state}</span>}
        {nz(row.asset_tag) && <span style={{ ...S.badge, background: "#f1f5f9", color: "#334155" }}>Tag: {row.asset_tag}</span>}
      </>}
    >
      {loadErr && <div style={S.err}><AlertTriangle size={14} /> Failed to load live data from SDP: {loadErr} — showing snapshot data.</div>}
      {errs.asset && <div style={S.err}><AlertTriangle size={14} /> {errs.asset} — showing snapshot data.</div>}

      <Card title="General">
        <Field label="Product" value={pick("product_name", a.product)} />
        <Field label="Manufacturer" value={nz(row.manufacturer) || dv(a.product && a.product.manufacturer)} />
        <Field label="Category" value={pick("category", a.category)} />
        <Field label="Serial Number" value={pick("serial_number", a.serial_number)} />
        <Field label="Service Tag" value={nz(row.service_tag)} />
        <Field label="Barcode" value={pick("barcode", a.barcode)} />
        <Field label="Site" value={pick("site", a.site)} />
        <Field label="Region" value={pick("region", a.region)} />
        <Field label="Location" value={pick("location", a.location)} />
        <Field label="Department" value={pick("department", a.department)} />
        <Field label="Created" value={pick("created_time", a.created_time)} />
        <Field label="Created By" value={pick("created_by", a.created_by)} />
        <Field label="Last Updated" value={dv(a.last_updated_time)} />
      </Card>

      <Card title="Assignment">
        <Field label="Assigned User" value={nz(row.assigned_user) || dv(user.name)} />
        <Field label="User Email" value={nz(row.assigned_user_email) || user.email_id} />
        <Field label="Last Logged User" value={nz(row.last_logged_user)} />
        <Field label="Used By Asset" value={dv(a.used_by_asset)} />
        <Field label="Loanable" value={a.is_loanable || row.is_loanable === 1 ? "Yes" : ""} />
        <Field label="On Loan" value={a.is_loaned || row.is_loaned === 1 ? "Yes" : ""} />
      </Card>

      <Card title="Hardware & OS">
        <Field label="Model" value={nz(row.system_model)} />
        <Field label="OS" value={[nz(row.os), nz(row.os_version)].filter(Boolean).join(" ")} />
        <Field label="OS Build" value={nz(row.os_build)} />
        <Field label="Processor" value={nz(row.processor)} />
        <Field label="Processor Count" value={nz(row.processor_count)} />
        <Field label="RAM" value={gb(row.ram_gb)} />
        <Field label="Disk" value={gb(row.disk_gb)} />
        <Field label="BIOS" value={nz(row.bios_version)} />
        <Field label="Domain" value={nz(row.domain)} />
      </Card>

      <Card title="Network">
        <Field label="IP Address" value={pick("ip_address", a.ip_address)} />
        <Field label="MAC Address" value={pick("mac_address", a.mac_address)} />
        {adapters.map((n, i) => (
          <Field key={i} label={`Adapter ${i + 1}`} value={[dv(n.name) || dv(n.description), n.ip_address, n.mac_address].filter(Boolean).join(" · ")} />
        ))}
      </Card>

      <Card title="Financial & Warranty">
        <Field label="Vendor" value={pick("vendor", a.vendor)} />
        <Field label="Purchase Cost" value={money(nz(row.purchase_cost) || a.purchase_cost)} />
        <Field label="Current Cost" value={money(nz(row.current_cost) || a.current_cost)} />
        <Field label="Total Cost" value={money(nz(row.total_cost) || a.total_cost)} />
        <Field label="Acquisition Date" value={pick("acquisition_date", a.acquisition_date)} />
        <Field label="Warranty Expiry" value={pick("warranty_expiry", a.warranty_expiry)} />
        <Field label="Expiry Date" value={dv(a.expiry_date)} />
        <Field label="Age" value={dv(a.age)} />
      </Card>

      <Card title="Audit / Scan">
        <Field label="Last Audit Status" value={nz(row.last_audit_status) || dv(lastAudit.status)} />
        <Field label="Last Audit Time" value={nz(row.last_audit_time) || dv(lastAudit.audit_time || lastAudit.time)} />
        <Field label="Last Audit Note" value={nz(row.last_audit_description) || dv(lastAudit.description)} />
        <Field label="Scan Source" value={pick("recent_scan_source", a.recent_scan_source)} />
        <Field label="Snapshot" value={nz(row.snapshot_at)} />
      </Card>

      {Array.isArray(a.attachments) && a.attachments.length > 0 && (
        <div style={S.card}>
          <div style={S.h}>Attachments</div>
          <Attachments list={a.attachments} module="assets" id={row.id} />
        </div>
      )}

      <div style={S.card}>
        <div style={S.h}>Related Tickets {live ? `(${related.length})` : ""}</div>
        {loading && !live && <div style={S.muted}>Loading…</div>}
        {errs.related && <div style={S.err}><AlertTriangle size={14} /> {errs.related}</div>}
        {live && !related.length && !errs.related && <div style={S.muted}>No related tickets found.</div>}
        {related.map((t, i) => (
          <div
            key={t.id}
            onClick={() => onOpenTicket && onOpenTicket(t)}
            style={{ ...(i ? S.item : {}), cursor: onOpenTicket ? "pointer" : "default", display: "flex", gap: 10, alignItems: "baseline" }}
            title="Open ticket"
          >
            <span style={{ fontWeight: 700, color: "rgb(var(--brand-700))", minWidth: 52 }}>#{t.display_id}</span>
            <span style={{ flex: 1, fontSize: 13, color: "#0f172a" }}>{t.subject}</span>
            {t.linked_to_asset && <span style={{ ...S.badge, background: "#ede9fe", color: "#5b21b6" }}>Linked</span>}
            <span style={{ ...S.badge, ...statusColor(t.status) }}>{t.status}</span>
            <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{t.created_time}</span>
          </div>
        ))}
        {live && related.length > 0 && (
          <div style={{ ...S.muted, marginTop: 8 }}>Tickets linked to this asset or raised by its assigned user.</div>
        )}
      </div>

      {live && live.fetched_at && <div style={{ ...S.muted, textAlign: "right" }}>Live from SDP · {new Date(live.fetched_at).toLocaleString()}</div>}
    </DrawerShell>
  );
}
